import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, ChevronDown, Plus, RefreshCw, Share2, Filter as FilterIcon } from 'lucide-react';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import AgentRunsQueryBuilder, { buildModularFilters } from 'features/agent-analytics/components/AgentRunsQueryBuilder';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import DashboardGrid from 'features/dashboards/components/DashboardGrid';
import DashboardCard from 'features/dashboards/components/DashboardCard';
import AddCardDialog from 'features/dashboards/components/AddCardDialog';
import ShareDialog from 'features/dashboards/components/ShareDialog';
import EditorsDialog from 'features/dashboards/components/EditorsDialog';
import Menu from 'features/dashboards/components/Menu';
import { ChartTypeDialog, TextCardDialog } from 'features/dashboards/components/CardEditDialogs';
import { chartLabel } from 'features/dashboards/chartTypes';
import DashboardDetailsDialog from 'features/dashboards/components/DashboardDetailsDialog';
import * as api from 'features/dashboards/api/dashboardsApi';
import { errorText, presetFromWindow, windowFromPreset, storedWindow } from 'features/dashboards/format';
import followRun from 'features/dashboards/followRun';
import usePolling from 'shared/hooks/usePolling';

const REFRESH_CHOICES = [[0, 'Off'], [30, 'Every 30s'], [60, 'Every minute'], [300, 'Every 5 minutes']];
const REFRESH_SHORT = { 30: '30s', 60: '1m', 300: '5m' };

function readJsonParam(sp, key) {
  try { const v = JSON.parse(decodeURIComponent(sp.get(key) || '')); return Array.isArray(v) ? v : []; } catch { return []; }
}

export default function DashboardViewPage({ dashboardId, overview = false }) {
  const { team } = useParams();
  const navigate = useNavigate();
  const { confirm: appConfirm } = useAppDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dash, setDash] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [menu, setMenu] = useState(null);
  // Names are edited where they are shown: a tab in the strip, a card in its header.
  const [renamingTab, setRenamingTab] = useState(null);
  const [renamingCard, setRenamingCard] = useState(null);
  const base = `/${encodeURIComponent(team)}/reports`;

  useBreadcrumb(overview ? [{ label: 'Overview' }] : [{ label: 'Reports', to: base }, ...(dash ? [{ label: dash.name }] : [])]);
  useEffect(() => { if (dash) document.title = overview ? 'Overview' : dash.name; }, [dash, overview]);

  const load = useCallback(async () => {
    try { setDash(await api.getDashboard(team, dashboardId)); setError(null); } catch (e) { setError(errorText(e)); }
  }, [team, dashboardId]);
  useEffect(() => { load(); }, [load]);

  // Live while Ask works on this dashboard (TRO-469): a change it makes is an
  // event, from the conversation on this page (trodo:dashboard) or, when the
  // conversation lives elsewhere, from the run's own stream (?run=). Several
  // changes in a burst are one reload.
  const reloadTimer = useRef(null);
  const reloadSoon = useCallback(() => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(load, 250);
  }, [load]);
  useEffect(() => () => clearTimeout(reloadTimer.current), []);
  useEffect(() => {
    const onDashboard = (e) => { if (String(e.detail?.dashboard_id) === String(dashboardId)) reloadSoon(); };
    window.addEventListener('trodo:dashboard', onDashboard);
    return () => window.removeEventListener('trodo:dashboard', onDashboard);
  }, [dashboardId, reloadSoon]);
  const runId = searchParams.get('run');
  useEffect(() => {
    if (!runId) return undefined;
    return followRun(runId, team, (type, data) => {
      if (type === 'dashboard' && String(data?.dashboard_id) === String(dashboardId)) reloadSoon();
    });
  }, [runId, team, dashboardId, reloadSoon]);

  // ── URL state: tab, window, filters — a view is a link ────────────────
  const setParam = useCallback((key, value) => {
    setSearchParams((p) => { const n = new URLSearchParams(p); if (value == null || value === '') n.delete(key); else n.set(key, value); return n; }, { replace: true });
  }, [setSearchParams]);

  const tabs = useMemo(() => dash?.tabs || [], [dash]);
  const tabId = tabs.some((t) => String(t.id) === searchParams.get('tab')) ? Number(searchParams.get('tab')) : tabs[0]?.id;

  const preset = searchParams.get('t') || (dash ? presetFromWindow(dash.window) : '7d');
  const [custom, setCustom] = useState({ from: '', to: '' });

  // Auto-refresh: the URL's choice, else the dashboard's. Each tick moves a
  // relative window to now; nothing runs while the tab is hidden, and coming
  // back to it after a missed tick catches up at once.
  const refreshParam = searchParams.get('refresh');
  const refresh = refreshParam != null ? (Number(refreshParam) || null) : (dash?.refresh_seconds || null);
  const [tick, setTick] = useState(0);
  usePolling(() => { setTick((n) => n + 1); }, {
    enabled: Boolean(refresh), interval: (refresh || 0) * 1000, immediate: false, focusMinGap: (refresh || 0) * 1000,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const range = useMemo(() => windowFromPreset(preset, custom), [preset, custom, tick]);

  const [selectedAgents, setSelectedAgents] = useState(() => readJsonParam(searchParams, 'agents'));
  const [agentScopedFilters, setAgentScopedFilters] = useState(() => readJsonParam(searchParams, 'agentFilters'));
  const [globalFilters, setGlobalFilters] = useState(() => readJsonParam(searchParams, 'globalFilters'));
  const [filterShown, setFilterShown] = useState(() => selectedAgents.length + agentScopedFilters.length + globalFilters.length > 0);
  useEffect(() => {
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      const put = (k, arr) => (arr.length ? n.set(k, encodeURIComponent(JSON.stringify(arr))) : n.delete(k));
      put('agents', selectedAgents); put('agentFilters', agentScopedFilters); put('globalFilters', globalFilters);
      return n;
    }, { replace: true });
  }, [selectedAgents, agentScopedFilters, globalFilters, setSearchParams]);
  const filters = useMemo(
    () => buildModularFilters({ selectedAgents, agentScopedFilters, globalFilters }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selectedAgents), JSON.stringify(agentScopedFilters), JSON.stringify(globalFilters)],
  );

  // A window change while editing becomes the dashboard's default window.
  const onTimeChange = useCallback((next) => {
    const p = next?.preset || '7d';
    setParam('t', p);
    setCustom({ from: next?.from || '', to: next?.to || '' });
    if (editing && p !== 'custom') {
      api.updateDashboard(team, dashboardId, { window: storedWindow(p) }).catch(() => {});
    }
  }, [editing, setParam, team, dashboardId]);

  const onRefreshMenu = (anchor) => setMenu({
    anchor,
    items: REFRESH_CHOICES.map(([secs, label]) => ({
      label,
      checked: (secs || null) === refresh,
      onClick: () => {
        if (editing) {
          setParam('refresh', null);
          mutate(() => api.updateDashboard(team, dashboardId, { refresh_seconds: secs || null }));
        } else {
          setParam('refresh', String(secs));
        }
      },
    })),
  });

  // ── cards ─────────────────────────────────────────────────────────────
  const runKey = useMemo(() => JSON.stringify([range.from.slice(0, 16), range.to.slice(0, 16), filters]), [range, filters]);
  const run = useCallback((cardId) => api.runCard(team, dashboardId, cardId, range, filters), [team, dashboardId, range, filters]);
  const cards = useMemo(() => (dash?.cards || []).filter((c) => c.tab_id === tabId), [dash, tabId]);
  const layout = useMemo(() => cards.map((c) => ({ i: String(c.id), x: c.x, y: c.y, w: c.w, h: c.h, minW: 2, minH: 2 })), [cards]);

  const saveTimer = useRef(null);
  const onLayoutChange = useCallback((next) => {
    if (!editing || !Array.isArray(next)) return;
    const byId = new Map(cards.map((c) => [String(c.id), c]));
    const changed = next.filter((l) => { const c = byId.get(l.i); return c && (c.x !== l.x || c.y !== l.y || c.w !== l.w || c.h !== l.h); });
    if (!changed.length) return;
    setDash((d) => ({ ...d, cards: d.cards.map((c) => { const l = next.find((x) => x.i === String(c.id)); return l ? { ...c, x: l.x, y: l.y, w: l.w, h: l.h } : c; }) }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.saveLayout(team, dashboardId, next.map((l) => ({ id: Number(l.i), x: l.x, y: l.y, w: l.w, h: l.h }))).catch((e) => setError(errorText(e)));
    }, 500);
  }, [editing, cards, team, dashboardId]);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const mutate = useCallback(async (fn) => {
    try { await fn(); await load(); } catch (e) { setError(errorText(e)); }
  }, [load]);

  const onCardMenu = useCallback((card, anchor) => {
    setMenu({
      anchor,
      items: [
        ...(card.kind === 'text'
          ? [{ label: 'Edit text', onClick: () => setDialog({ kind: 'text', card }) }]
          : [{ label: `Chart type: ${chartLabel(card.viz?.chart)}`, onClick: () => setDialog({ kind: 'chart', card }) }]),
        { label: 'Rename', onClick: () => setRenamingCard(card.id) },
        ...tabs.filter((t) => t.id !== card.tab_id).map((t) => ({ label: `Move to ${t.name}`, onClick: () => mutate(() => api.updateCard(team, dashboardId, card.id, { tab_id: t.id, y: 1000 })) })),
        null,
        { label: 'Remove', danger: true, onClick: () => mutate(() => api.deleteCard(team, dashboardId, card.id)) },
      ],
    });
  }, [mutate, tabs, team, dashboardId]);

  // "Ask about this": Ask opens beside the page with the card named; the
  // page itself (dashboard, tab, window, filters) rides in its screen context.
  const onAskAbout = useCallback((card) => {
    const title = card.viz?.title || 'this card';
    window.dispatchEvent(new CustomEvent('trodo:ask:open', { detail: { prompt: `About the "${title}" card: ` } }));
  }, []);

  // ── tabs ──────────────────────────────────────────────────────────────
  const addTab = async () => {
    try { const { id } = await api.addTab(team, dashboardId, 'New tab'); await load(); setParam('tab', String(id)); setRenamingTab(id); } catch (e) { setError(errorText(e)); }
  };
  const renameTab = (t, name) => {
    setRenamingTab(null);
    if (name && name.trim() && name.trim() !== t.name) mutate(() => api.renameTab(team, dashboardId, t.id, name.trim()));
  };
  const renameCard = (card, title) => {
    setRenamingCard(null);
    if (title != null && title.trim() !== (card.viz?.title || '')) mutate(() => api.updateCard(team, dashboardId, card.id, { viz: { ...card.viz, title: title.trim() } }));
  };
  const onTabMenu = (t, anchor) => setMenu({
    anchor,
    items: [
      { label: 'Rename', onClick: () => setRenamingTab(t.id) },
      { label: 'Delete', danger: true, disabled: tabs.length <= 1, onClick: async () => {
        if (await appConfirm(`"${t.name}" and its cards will be removed.`, { title: 'Delete this tab?', confirmLabel: 'Delete', danger: true })) {
          mutate(() => api.deleteTab(team, dashboardId, t.id)); setParam('tab', null);
        }
      } },
    ],
  });

  // ── the dashboard menu ────────────────────────────────────────────────
  const onDashMenu = (anchor) => setMenu({
    anchor,
    items: [
      { label: 'Edit board', onClick: () => setEditing(true) },
      { label: 'Edit properties', onClick: () => setDialog('details') },
      ...(dash.is_owner ? [{ label: 'Editors', onClick: () => setDialog('editors') }] : []),
      ...(dash.is_default ? [{ label: 'Reset to default', onClick: async () => {
        if (await appConfirm('Tabs and cards go back to the default set.', { title: 'Reset the Overview?', confirmLabel: 'Reset' })) mutate(() => api.resetDashboard(team, dashboardId));
      } }] : []),
      ...(dash.is_owner && !dash.is_default ? [null, { label: 'Delete', danger: true, onClick: async () => {
        if (await appConfirm(`"${dash.name}" will be deleted for everyone on the team.`, { title: 'Delete this report?', confirmLabel: 'Delete', danger: true })) {
          try { await api.deleteDashboard(team, dashboardId); navigate(base); } catch (e) { setError(errorText(e)); }
        }
      } }] : []),
    ],
  });

  if (error && !dash) {
    return (
      <main className="flex flex-1 items-center justify-center bg-surface p-8" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="max-w-md rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-4 py-3 text-[13px] text-danger">{error}</div>
      </main>
    );
  }
  if (!dash) {
    return <main className="flex flex-1 items-center justify-center bg-surface"><TrodoLoader label={overview ? 'Loading the overview' : 'Loading the report'} /></main>;
  }

  const activeFilters = selectedAgents.length + agentScopedFilters.length + globalFilters.length;

  return (
    <main className="relative flex flex-1 min-h-0 flex-col overflow-y-auto bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="sticky top-0 z-20 border-b border-[color:var(--border-default)] bg-surface">
        <div className="flex min-h-[44px] flex-wrap items-center gap-1 px-4">
          <nav className="flex min-w-0 items-center gap-1 overflow-x-auto" aria-label="Tabs">
            {tabs.map((t) => (renamingTab === t.id ? (
              // eslint-disable-next-line jsx-a11y/no-autofocus
              <input key={t.id} autoFocus defaultValue={t.name} aria-label="Tab name" maxLength={60}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => renameTab(t, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenamingTab(null); }}
                style={{ width: `${Math.max(6, t.name.length + 2)}ch` }}
                className="my-1.5 shrink-0 rounded border border-[color:var(--brand-primary)] bg-surface px-2 py-1 text-[13px] font-medium text-[color:var(--text-primary)] outline-none" />
            ) : (
              <button key={t.id} type="button" onClick={() => setParam('tab', String(t.id))}
                onContextMenu={(e) => { if (editing) { e.preventDefault(); onTabMenu(t, e.currentTarget); } }}
                onDoubleClick={() => { if (editing) setRenamingTab(t.id); }}
                aria-current={t.id === tabId ? 'page' : undefined}
                className={`my-1.5 shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors ${t.id === tabId ? 'bg-surface-selected text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`}>
                {t.name}
              </button>
            )))}
            {editing ? (
              <button type="button" onClick={addTab} aria-label="Add tab" className="shrink-0 rounded p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
                <Plus className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </nav>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-2 py-1.5">
            <EventsTimeFilter selectedPreset={preset} onTimeFilterChange={onTimeChange} menuAlign="right" />
            <button type="button" aria-label="Auto-refresh" title="Auto-refresh" onClick={(e) => onRefreshMenu(e.currentTarget)} className={activityToolbarButtonClass}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              {refresh ? REFRESH_SHORT[refresh] : null}
            </button>
            <button type="button" aria-label="Filter" title="Filter" onClick={() => setFilterShown((v) => !v)} className={activityToolbarButtonClass}>
              <FilterIcon size={14} color="var(--text-muted)" />
              <span className="hidden lg:inline">Filter</span>
              {activeFilters > 0 ? <span className="rounded-full bg-surface-hover px-1.5 text-[10px] font-semibold text-[color:var(--text-primary)]">{activeFilters}</span> : null}
            </button>
            {dash.can_edit ? (
              <>
                {editing ? (
                  <button type="button" onClick={() => setDialog('add')} className={activityToolbarButtonClass}>
                    <Plus className="h-3.5 w-3.5" aria-hidden /> Add card
                  </button>
                ) : null}
                <button type="button" aria-label="Share" title="Share" onClick={() => setDialog('share')} className={activityToolbarButtonClass}>
                  <Share2 className="h-3.5 w-3.5" aria-hidden /><span className="hidden lg:inline">Share</span>
                </button>
                {editing ? (
                  <button type="button" onClick={() => setEditing(false)}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
                    <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />Done
                  </button>
                ) : (
                  <button type="button" aria-haspopup="menu" aria-expanded={Boolean(menu)} onClick={(e) => onDashMenu(e.currentTarget)} className={activityToolbarButtonClass}>
                    Edit<ChevronDown className="h-3.5 w-3.5 text-[color:var(--text-muted)]" aria-hidden />
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="shrink-0 px-4 pt-3 empty:hidden">
        <AgentRunsQueryBuilder
          teamId={team}
          selectedAgents={selectedAgents}
          onSelectedAgentsChange={setSelectedAgents}
          agentScopedFilters={agentScopedFilters}
          onAgentScopedFiltersChange={setAgentScopedFilters}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          shown={filterShown}
        />
      </div>

      {error ? <div className="mx-4 mt-3 rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12.5px] text-[color:var(--feedback-error)]">{error}</div> : null}

      <div className="min-h-0 flex-1 px-3 pb-10 pt-3">
        {cards.length === 0 ? (
          <div className="flex h-[40vh] items-center justify-center text-[12px] text-[color:var(--text-muted)]">
            {dash.can_edit ? (
              <button type="button" onClick={() => { setEditing(true); setDialog('add'); }} className={activityToolbarButtonClass}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> Add card
              </button>
            ) : 'No cards on this tab'}
          </div>
        ) : (
          <DashboardGrid key={tabId} layout={layout} editing={editing} onLayoutChange={onLayoutChange}>
            {cards.map((c) => (
              <div key={String(c.id)}>
                <DashboardCard card={c} run={run} runKey={runKey} editing={editing} onMenu={onCardMenu} onAsk={onAskAbout}
                  renaming={renamingCard === c.id} onRename={editing ? (title) => renameCard(c, title) : null} onStartRename={editing ? () => setRenamingCard(c.id) : null}
                  onEditText={editing && c.kind === 'text' ? () => setDialog({ kind: 'text', card: c }) : null} />
              </div>
            ))}
          </DashboardGrid>
        )}
      </div>

      {menu ? <Menu anchor={menu.anchor} items={menu.items} onClose={() => setMenu(null)} /> : null}
      {dialog === 'add' ? (
        <AddCardDialog team={team} range={range} onClose={() => setDialog(null)}
          onAdd={async (card) => { await api.addCard(team, dashboardId, { ...card, tab_id: tabId }); await load(); }} />
      ) : null}
      {dialog && dialog.kind === 'chart' ? (
        <ChartTypeDialog card={dialog.card} onClose={() => setDialog(null)}
          onSave={async (patch) => { await api.updateCard(team, dashboardId, dialog.card.id, patch); await load(); }} />
      ) : null}
      {dialog && dialog.kind === 'text' ? (
        <TextCardDialog card={dialog.card} onClose={() => setDialog(null)}
          onSave={async (patch) => { await api.updateCard(team, dashboardId, dialog.card.id, patch); await load(); }} />
      ) : null}
      {dialog === 'details' ? (
        <DashboardDetailsDialog title={overview ? 'Overview details' : 'Report details'} confirmLabel="Save" initial={{ name: dash.name, description: dash.description }}
          onClose={() => setDialog(null)}
          onSubmit={async (v) => { await api.updateDashboard(team, dashboardId, v); await load(); }} />
      ) : null}
      {dialog === 'share' ? <ShareDialog team={team} dashboard={dash} onChanged={load} onClose={() => setDialog(null)} /> : null}
      {dialog === 'editors' ? <EditorsDialog team={team} dashboard={dash} onChanged={load} onClose={() => setDialog(null)} /> : null}
    </main>
  );
}
